import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
} from '@angular/core';
import { IconComponent } from '../../atoms/icon/icon.component';
import { BadgeCopyComponent } from './rsabt-indicator-copy.component';
import { IndicatorState } from '../../../models/indicator.models';
import { IndicatorStates } from '../../../constants/indicators.constants';
import { TooltipDirective } from '../../atoms/tooltip/tooltip.directive';
import { truncate } from '@ui';

@Component({
  selector: 'ui-event-center-indicator',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [BadgeCopyComponent, IconComponent, TooltipDirective],
  template: `
    <div class="relative">
      @if (isDanger()) {
        <div class="absolute top-0 -left-[18px] text-error">
          <ui-icon icon="exclamation-circle" theme="fill" />
        </div>
      }
      <div class="flex flex-col gap-y-1">
        <div class="flex gap-x-2">
          @if (showCenterCode()) {
            <span
              class="flex items-center justify-center text-[10px] rounded uppercase px-1 whitespace-nowrap"
              [class]="stateClassMap[state()].code"
            >
              {{ distributorId() }}
            </span>
          }
          <span
            uiTooltip
            [tooltipTitle]="nameTemplate"
            class="text-sm font-medium uppercase duration-300 transition-colors"
            [class]="stateClassMap[state()].label"
            (click)="emitCenter()"
          >
            {{ truncatedName() }}
          </span>
        </div>
        <ui-badge-copy [id]="centerId()!" [disabled]="isDisabled()" />
      </div>
    </div>

    <ng-template #nameTemplate>
      {{ name() }}
    </ng-template>
  `,
})
export class EventCenterIndicatorComponent {
  protected readonly TRUNCATE_MAX_CHARS: number = 72;

  state = input<IndicatorState>(IndicatorStates.Default);
  showCenterCode = input<boolean>(false);
  name = input.required<string>();
  distributorId = input.required<string>();
  centerId = input.required<string>();
  rsabtId = input<string>();
  tsabtId = input<string>();
  vtnId = input<string>();
  transformerId = input<string>();

  onCenter = output<{
    distributorId: string;
    centerId: string;
    rsabtId: string;
    tsabtId: string;
    vtnId: string;
  }>();

  isDanger = computed(() => this.state() === IndicatorStates.Danger);
  isDisabled = computed(() => this.state() === IndicatorStates.Disabled);
  truncatedName = computed(() => {
    const maxChars = this.showCenterCode()
      ? this.TRUNCATE_MAX_CHARS
      : this.TRUNCATE_MAX_CHARS + 5;
    return truncate(this.name()!, maxChars);
  });

  emitCenter() {
    this.onCenter.emit({
      distributorId: this.distributorId()!,
      centerId: this.centerId()!,
      rsabtId: this.rsabtId()!,
      tsabtId: this.tsabtId()!,
      vtnId: this.vtnId()!,
    });
  }

  stateClassMap: Record<IndicatorState, { code: string; label: string }> = {
    active: {
      code: 'text-black bg-[#D6E4FF]',
      label: 'cursor-pointer text-primary hover:text-tertiary',
    },
    disabled: { code: 'bg-neutral-1 text-neutral-6', label: 'text-neutral-6' },
    danger: {
      code: 'text-black bg-red-2',
      label: 'cursor-pointer text-error hover:text-black',
    },
  };
}
